import { useEffect, useState } from 'react';
import { Avatar, useTheme, Box } from '@mui/material';
import { Tooltip, Icon } from '@components/Outputs';
import ImageUpload from '../../0_pages/DataBase/Documents/ImageUpload';
import { uploadFile } from '@services/ApiService';
import { useAppDispatch } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { CEntity, IEntity, IIcon } from '@ts/ui-models';
import { Grid, IconButton } from '@ts/storybook';
import { TMode } from '@ts/ui-models';

interface IAvatarImageProps {
  mode: TMode;
  onClearImage: () => void;
  onUploadedPhoto: (image: any) => void;
  image?: string;
  cEntity?: CEntity<IEntity>;
  entityTypeId: 'tsElement' | 'taUser' | 'tsOrganization' | 'tsElementType';
  entityId: string;
}

const AvatarImage = (props: IAvatarImageProps) => {
  const { entityId } = props;
  const purposeId = 'default';
  const unknownImage = require(`@public/unknown.png`);
  const isViewMode = props.mode === 'view';
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [hasImage, setHasImage] = useState(Boolean(props.image));

  const [state, setState] = useState({
    photoFile: null,
    showModalPhoto: false,
  });

  const handleEdit = () => setState({ photoFile: null, showModalPhoto: true });

  const _handleUploadPhoto = async ({ imageName, photoFile }) => {
    if (photoFile != null) {
      const document = await uploadFile(
        dispatch,
        {
          name: imageName,
          purposeId,
          entityTypeId: props.entityTypeId,
          entityId,
          mediaType: photoFile.type,
          isSystem: false,
        },
        photoFile
      );

      if (document) props.onUploadedPhoto(document);
    } else {
      dispatch(globalActions.notification('No file.'));
    }
    setState({ ...state, showModalPhoto: false });
  };

  const handleDelete = () => {
    if (props.image) props.onClearImage();
    setHasImage(false);
  };

  useEffect(() => {
    setHasImage(Boolean(props.image));
  }, [props.image]);

  const icon: IIcon | string = props.cEntity?.getIcon() ?? '';

  return (
    <Grid container justifyContent="center">
      <Box
        sx={{
          position: 'relative',
          width: theme.spacing(15),
          height: theme.spacing(15),
          zIndex: 1,
          '&:hover .overlay': {
            opacity: 1,
          },
        }}
      >
        {hasImage ? (
          <Avatar
            src={props.image}
            alt="Avatar"
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              boxShadow: `0px 5px 20px ${theme.palette.primary.main}`,
              '& img': { objectFit: 'scale-down' },
            }}
          />
        ) : props.entityTypeId === 'taUser' ? (
          <Avatar
            src={unknownImage}
            alt="Avatar"
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              boxShadow: `0px 5px 20px ${theme.palette.primary.main}`,
              '& img': { objectFit: 'scale-down' },
            }}
          />
        ) : (
          <Box
            sx={{
              padding: '20px',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              boxShadow: `0px 5px 20px ${theme.palette.primary.main}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& img': { objectFit: 'scale-down' },
            }}
          >
            {props.cEntity && <Icon icon={icon} color="gray" size={80} />}
          </Box>
        )}

        <Box
          className={isViewMode ? 'overlay-hidden' : 'overlay'}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            opacity: isViewMode ? 0 : 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.3s ease',
            zIndex: 2,
          }}
        >
          {hasImage && !isViewMode ? (
            <>
              <Tooltip title="Edit">
                <IconButton icon="edit" disabled={isViewMode} sx={{ color: 'white', m: 1 }} onClick={handleEdit} />
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton icon="delete" disabled={isViewMode} sx={{ color: 'white', m: 1 }} onClick={handleDelete} />
              </Tooltip>
            </>
          ) : !isViewMode ? (
            <Tooltip title="Upload image">
              <IconButton icon="edit" sx={{ color: 'white', m: 1 }} onClick={handleEdit} />
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <ImageUpload
        openModal={state.showModalPhoto}
        purposeId={purposeId}
        imageName={`Profile picture - ${props.entityId}`}
        onUploadPhoto={_handleUploadPhoto}
        onClose={() => setState({ ...state, showModalPhoto: false })}
      />
    </Grid>
  );
};

export default AvatarImage;
